import { ManagerBase, MouseEventResult } from './manager-base';
import { LevelDesignerComponent } from '../level-designer/level-designer.component';
import { PolygonElement } from '../elements/polygon-element';

export class PolygonPointsManager extends ManagerBase {
    private isDraggingPoint: boolean = false;
    private draggedPolygon: PolygonElement | null = null;
    private draggedPointIndex: number = -1;
    private lastMouseX: number = 0;
    private lastMouseY: number = 0;

    // New properties for handle sizes
    private vertexHandleSize: number = 10;
    private midpointHandleSize: number = 6;

    private selectedPointIndex: number = -1;
    private hoveredPointIndex: number = -1;
    private hoveredMidpointindex: number = -1;
    private hoveredPolygon : PolygonElement | null = null;
    private readonly pointRadius: number = 5;

    constructor(levelDesigner: LevelDesignerComponent, canvas: HTMLCanvasElement) {
        super(levelDesigner, canvas);
    }

    public OnDraw(): void {
        const activePolygons = this.getActivePolygons();
        activePolygons.forEach(polygon => {
            this.drawVertexIndicators(polygon);
            this.drawMidpointIndicators(polygon);
        });
    }

    public override handleMouseDown(event: MouseEvent): MouseEventResult | undefined {
        const activePolygons = this.getActivePolygons();
        if (activePolygons.length === 0) return undefined;

        const mousePos = this.getMousePosition(event);
        
        for (const polygon of activePolygons) {
            // Check midpoints first
            const midpointIndex = this.isPointNearMidpoint(polygon, mousePos.x, mousePos.y);
            if (midpointIndex !== -1) {
                polygon.insertPointAtMidpoint(midpointIndex);
                this.isDraggingPoint = true;
                this.draggedPolygon = polygon;
                this.draggedPointIndex = midpointIndex + 1;
                this.lastMouseX = mousePos.x;
                this.lastMouseY = mousePos.y;
                polygon.startDraggingPoint(this.draggedPointIndex);
                this.levelDesigner.redraw();
                return { cursor: 'move', stopPropagation: true, redraw: true };
            }

            // Then check vertices
            const pointIndex = this.isPointNearVertex(polygon, mousePos.x, mousePos.y);
            if (pointIndex !== -1) {
                if (event.ctrlKey) {
                    // Don't allow removal if it would make the polygon invalid
                    if (polygon.getPoints().length > 3) {
                        console.log('Removing point at index', pointIndex);
                        polygon.removePoint(pointIndex);
                        this.levelDesigner.saveState();
                        this.levelDesigner.redraw();
                    } else {
                        console.log('Cannot remove point - polygon must have at least 3 points');
                    }
                } else {
                    this.isDraggingPoint = true;
                    this.draggedPolygon = polygon;
                    this.draggedPointIndex = pointIndex;
                    this.lastMouseX = mousePos.x;
                    this.lastMouseY = mousePos.y;
                    polygon.startDraggingPoint(pointIndex);
                }
                return { cursor: event.ctrlKey ? 'no-drop' : 'move', stopPropagation: true, redraw: true };
            }
        }
        return undefined;
    }

    public override handleMouseMove(event: MouseEvent): MouseEventResult | undefined {
        const mousePos = this.getMousePosition(event);

        if (this.isDraggingPoint && this.draggedPolygon) {
            const deltaX = mousePos.x - this.lastMouseX;
            const deltaY = mousePos.y - this.lastMouseY;
            
            this.draggedPolygon.dragPoint(deltaX, deltaY);
            
            this.lastMouseX = mousePos.x;
            this.lastMouseY = mousePos.y;
            
            return { cursor: 'move', stopPropagation: true, redraw: true };
        }
        
        const hoverePolygonWasSet : Boolean = this.hoveredPolygon != null; 
        this.hoveredPolygon = null;

        // Check for midpoints first
        const activePolygons = this.getActivePolygons();
        for (const polygon of activePolygons) {
            const i = this.isPointNearMidpoint(polygon, mousePos.x, mousePos.y);
            if (i !== -1) {
                this.hoveredMidpointindex = i;
                this.hoveredPolygon = polygon;
                return { cursor: 'pointer', stopPropagation: true, redraw: true };
            }
        }
        
        // Then check vertices and line segments
        for (const polygon of activePolygons) {
            const pointIndex = this.isPointNearVertex(polygon, mousePos.x, mousePos.y);
            if (pointIndex !== -1) {
                this.hoveredPointIndex = pointIndex;
                this.hoveredPolygon = polygon;
                return { 
                    cursor: event.ctrlKey ? 'no-drop' : 'move', 
                    stopPropagation: true, 
                    redraw: true 
                };
            }
        }

        this.hoveredPointIndex = -1;
        return { cursor: 'default', stopPropagation: false, redraw: hoverePolygonWasSet == true };
    }

    public override handleMouseUp(event: MouseEvent): MouseEventResult | undefined {
        if (this.isDraggingPoint) {
            this.isDraggingPoint = false;
            if (this.draggedPolygon) {
                this.draggedPolygon.stopDraggingPoint();
                this.draggedPolygon = null;
            }
            this.draggedPointIndex = -1;
            this.levelDesigner.saveState();
            return { cursor: 'default', stopPropagation: true, redraw: false };
        }
        return undefined;
    }

    private drawVertexIndicators(polygon: PolygonElement): void {
        polygon.getPoints().forEach((point, index) => {
            this.ctx.fillStyle = 'blue';
            if(this.hoveredPolygon == polygon && this.hoveredPointIndex == index) { 
                this.ctx.fillStyle = 'red';
            }
            this.ctx.beginPath();
            this.ctx.arc(point.x, point.y, this.vertexHandleSize / 2 / this.levelDesigner.getScale(), 0, 2 * Math.PI);
            this.ctx.fill();
        });
    }

    private drawMidpointIndicators(polygon: PolygonElement): void {
        const points = polygon.getPoints();
        for (let i = 0; i < points.length; i++) {
            this.ctx.fillStyle = 'green';
            if(this.hoveredPolygon == polygon && this.hoveredMidpointindex == i && !this.isDraggingPoint) { 
                this.ctx.fillStyle = 'red';
            }
            const p1 = points[i];
            const p2 = points[(i + 1) % points.length];
            const midX = (p1.x + p2.x) / 2;
            const midY = (p1.y + p2.y) / 2;

            this.ctx.beginPath();
            this.ctx.arc(midX, midY, this.midpointHandleSize / 2 / this.levelDesigner.getScale(), 0, 2 * Math.PI);
            this.ctx.fill();
        }
    }

    private isPointNearVertex(polygon: PolygonElement, x: number, y: number): number {
        const points = polygon.getPoints();
        const threshold = this.vertexHandleSize / 2 / this.levelDesigner.getScale();
        
        for (let i = 0; i < points.length; i++) {
            const dx = points[i].x - x;
            const dy = points[i].y - y;
            if (dx * dx + dy * dy <= threshold * threshold) {
                return i;
            }
        }
        return -1;
    }

    private isPointNearMidpoint(polygon: PolygonElement, x: number, y: number): number {
        const points = polygon.getPoints();
        const threshold = (this.midpointHandleSize * 1.5) / this.levelDesigner.getScale();  // Increased hitbox
        
        for (let i = 0; i < points.length; i++) {
            const p1 = points[i];
            const p2 = points[(i + 1) % points.length];
            const midX = (p1.x + p2.x) / 2;
            const midY = (p1.y + p2.y) / 2;
            
            const dx = midX - x;
            const dy = midY - y;
            if (dx * dx + dy * dy <= threshold * threshold) {
                return i;
            }
        }
        return -1;
    }

    private getActivePolygons(): PolygonElement[] {
        // Locked polygons cannot be reshaped (unlock to extend, e.g. a hedge / tree-line).
        return this.levelDesigner.selectedElements.filter(element => element instanceof PolygonElement && !element.isLocked()) as PolygonElement[];
    }

    public override handleKeyDown(event: KeyboardEvent): MouseEventResult | undefined {
        if (event.key === 'Control' && this.hoveredPointIndex !== -1) {
            return { cursor: 'no-drop', stopPropagation: false, redraw: false };
        }
        return undefined;
    }

    public override handleKeyUp(event: KeyboardEvent): MouseEventResult | undefined {
        if (event.key === 'Control' && this.hoveredPointIndex !== -1) {
            return { cursor: 'move', stopPropagation: false, redraw: false };
        }
        return undefined;
    }
   
}
